/**
 * Safe return-target validation for the signed-out sign-in surface (KAN-156).
 *
 * Centralizes the `returnTo` contract so route tests and login behavior cannot
 * drift. A return target is valid only after canonical decoding and only when
 * it is `/` or matches an explicit protected app/admin React Router route
 * pattern. Anything else falls back to `/`. This module has no React/router
 * imports so it can be unit-tested in isolation.
 */

type RoutePattern = readonly string[];

/**
 * Protected app/admin route patterns mirrored from `apps/web/src/App.tsx`.
 * `:segment` entries are dynamic placeholders that match exactly one safe path
 * segment. Feature-gated admin routes are included unconditionally: a route
 * pattern remains a valid protected return-target candidate even when a flag
 * currently hides the component.
 *
 * Keep this list in sync with the protected route group in `App.tsx`.
 */
const PROTECTED_ROUTE_PATTERNS: RoutePattern[] = [
  [], // '/'
  ["dashboard"],
  ["vulnerabilities"],
  ["vulnerabilities", ":id"],
  ["threat-actors"],
  ["threat-actors", ":id"],
  ["threats", "leak-sites"],
  ["threats", "supply-chain"],
  ["providers"],
  ["providers", ":slug"],
  ["map"],
  ["analytics"],
  ["benchmarks"],
  ["benchmarks", ":id"],
  ["regulatory"],
  ["regulatory", "frameworks", ":slug"],
  ["regulatory", "calendar"],
  ["regulatory", "compare"],
  ["regulatory-actions"],
  ["stories"],
  ["stories", ":id"],
  ["articles"],
  ["settings"],
  ["admin"],
  ["admin", "content"],
  ["admin", "sources"],
  ["admin", "ai", "providers"],
  ["admin", "ai", "tasks"],
  ["admin", "ai", "costs"],
  ["admin", "email"],
  ["admin", "vendors"],
  ["admin", "vendors", "new"],
  ["admin", "system"],
  ["admin", "observability"],
  ["admin", "stories"],
  ["admin", "stories", ":id"],
  ["admin", "audit"],
  ["admin", "analytics"],
  ["admin", "benchmarks"],
  ["admin", "benchmarks", "new"],
  ["admin", "benchmarks", "pending"],
  ["admin", "benchmarks", ":id", "edit"],
  ["admin", "vulnerabilities"],
  ["admin", "vulnerabilities", ":id", "edit"],
  ["admin", "frameworks"],
  ["admin", "regulatory-briefs"],
  ["admin", "regulatory-briefs", ":id", "edit"],
  ["admin", "regulatory-deadlines", "new"],
  ["admin", "regulatory", "digest-runs"],
  ["admin", "db", "perf"],
  ["admin", "cpe-mappings"],
  ["admin", "ransomware-groups"],
  ["admin", "country-overrides"],
  ["admin", "research-review"],
];

const FALLBACK = "/";

/**
 * True when the string contains an ASCII control character (code point 0x00-0x1F
 * or 0x7F DEL). Implemented with charCodeAt rather than a control-char regex
 * literal so the source stays plain ASCII.
 */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/** A single decoded path segment is safe when it is a non-empty, non-traversal token. */
function isSafeSegment(segment: string): boolean {
  if (segment.length === 0) return false;
  if (segment === "." || segment === "..") return false;
  if (segment.includes("\\") || segment.includes("/")) return false;
  if (hasControlChar(segment)) return false;
  return true;
}

function matchesProtectedPattern(segments: string[]): boolean {
  return PROTECTED_ROUTE_PATTERNS.some((pattern) => {
    if (pattern.length !== segments.length) return false;
    return pattern.every((patternSegment, index) => {
      if (patternSegment.startsWith(":")) {
        // Dynamic placeholder: any safe single segment (validated upstream).
        return true;
      }
      return patternSegment === segments[index];
    });
  });
}

/**
 * Validate and canonicalize a candidate return target.
 *
 * Returns the safe relative path (with an optional preserved query string) when
 * the decoded path matches `/` or a protected route pattern; otherwise `/`.
 */
export function validateReturnTo(value: string | null | undefined): string {
  if (typeof value !== "string" || value.length === 0) return FALLBACK;

  // Reject control characters and backslashes anywhere in the raw value.
  if (hasControlChar(value) || value.includes("\\")) return FALLBACK;

  // Must be an origin-relative path, never protocol-relative or absolute.
  if (!value.startsWith("/") || value.startsWith("//")) return FALLBACK;

  // Strip the hash fragment, then split path from query.
  const withoutHash = value.split("#")[0];
  const queryIndex = withoutHash.indexOf("?");
  const rawPath =
    queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  // Preserve the original (undecoded) query string after the path validates.
  const rawQuery = queryIndex === -1 ? "" : withoutHash.slice(queryIndex);

  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(rawPath);
  } catch {
    // Malformed percent-encoding.
    return FALLBACK;
  }

  // Re-check for danger introduced by decoding (e.g. %2F%2F -> //, %5C -> \, %00).
  if (
    hasControlChar(decodedPath) ||
    decodedPath.includes("\\") ||
    decodedPath.includes("//") ||
    !decodedPath.startsWith("/")
  ) {
    return FALLBACK;
  }

  const segments = decodedPath === "/" ? [] : decodedPath.slice(1).split("/");
  if (!segments.every(isSafeSegment)) return FALLBACK;
  if (!matchesProtectedPattern(segments)) return FALLBACK;

  return decodedPath + rawQuery;
}

/**
 * Compute the safe return target from the current location.
 *
 * When the user is on the explicit `/login` route, the target comes from the
 * `returnTo` query parameter. Otherwise (the login surface rendered inline at a
 * protected deep link), the target is the current path + search.
 */
export function getSafeReturnToFromLocation(
  location: Pick<Location, "pathname" | "search"> = window.location,
): string {
  if (location.pathname === "/login") {
    const params = new URLSearchParams(location.search);
    return validateReturnTo(params.get("returnTo"));
  }
  return validateReturnTo(location.pathname + location.search);
}

/** Build an href to a public legal page, carrying a validated safe returnTo when present. */
export function buildPublicHref(
  path: "/privacy" | "/terms",
  returnTo?: string | null,
): string {
  const safe = validateReturnTo(returnTo);
  return safe === FALLBACK
    ? path
    : `${path}?returnTo=${encodeURIComponent(safe)}`;
}

/** Build an href to the public `/login` route, carrying a validated safe returnTo when present. */
export function buildLoginHref(returnTo?: string | null): string {
  const safe = validateReturnTo(returnTo);
  return safe === FALLBACK
    ? "/login"
    : `/login?returnTo=${encodeURIComponent(safe)}`;
}
